import { type ComponentProps, type FC, useRef } from 'react';
import {
  // biome-ignore lint/style/noRestrictedImports: not here
  useLocation as useWouterLocation,
  // biome-ignore lint/style/noRestrictedImports: not here
  Link as WouterLink,
  // biome-ignore lint/style/noRestrictedImports: not here
  Redirect as WouterRedirect,
} from 'wouter';
import { BASE, MODE, WORKSPACE, type WorkspaceType } from '@/base/constants';

// Wouter override
export const useLocation = () => {
  const [loc, wouterNavigate] = useWouterLocation();
  const isHardRedirected = useRef(false);
  const navigate = (
    ...args:
      | [string, { replace?: boolean }?]
      | [WorkspaceType | undefined, string, { replace?: boolean }?]
  ) => {
    let workspace: WorkspaceType | undefined;
    let target: string;
    let options: { replace?: boolean } | undefined;

    if (
      args.length === 1 ||
      (args.length === 2 && typeof args[1] === 'object')
    ) {
      target = args[0] as string;
      options = args[1] as { replace?: boolean };
    } else {
      workspace = args[0] as WorkspaceType | undefined;
      target = args[1] as string;
      options = args[2] as { replace?: boolean };
    }

    if (!workspace || workspace === WORKSPACE) {
      return wouterNavigate(target, options);
    }

    if (!isHardRedirected.current) {
      if (!target.startsWith('/') || target.includes('//')) {
        throw new Error('Invalid redirect path.');
      }
      isHardRedirected.current = true;
      window.location.replace(
        `${BASE ? `/${BASE}` : ''}${workspace === 'public' ? '' : `/${workspace}`}${target}`,
      );
    }
  };
  return [loc, navigate] as const;
};
export const Redirect: FC<
  ComponentProps<typeof WouterRedirect> & {
    workspace?: WorkspaceType;
  }
> = ({ workspace, ...props }) => {
  const target = props?.to || props?.href;
  const isHardRedirected = useRef(false);
  if (!target || !workspace || workspace === WORKSPACE)
    return <WouterRedirect {...props} />;

  if (!isHardRedirected.current) {
    if (!target.startsWith('/') || target.includes('//')) {
      throw new Error('Invalid redirect path.');
    }

    isHardRedirected.current = true;

    window.location.replace(
      `${BASE ? `/${BASE}` : ''}${workspace === 'public' ? '' : `/${workspace}`}${target}`,
    );
  }

  return null;
};
export const Link: FC<
  ComponentProps<typeof WouterLink> & { workspace?: WorkspaceType }
> = ({ workspace, ...props }) => {
  if (props.asChild && MODE === 'development')
    throw new Error('asChild is deprecated');
  const target = props?.to || props?.href;
  if (!target || !workspace || workspace === WORKSPACE)
    return <WouterLink {...(props as { to: string })} />;
  const { to: _, href: __, ...rest } = props;
  return (
    <a
      {...(rest as {})}
      href={`${BASE ? `/${BASE}` : ''}${workspace === 'public' ? '' : `/${workspace}`}${target}`}
    />
  );
};
